import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft, ChevronRight, Loader2, Save, AlertTriangle, IdCard, Repeat,
  Minus, Plus, CreditCard, Smartphone, Tablet, Wifi, Router, Package,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  enumerateOptions, evaluateOption, minOptionPrice, hasAnyEnabled,
} from "../lib/phonePricing";
import {
  tabletPricingFor, homeInternetPricingFor,
  tabletVariants, homeInternetVariants,
  tabletDeviceUsable, homeInternetDeviceUsable,
} from "../lib/linePricing";
import DeviceSelect from "../components/DeviceSelect";

const DEFAULT_MAX_LINES = 10;

// Cross-category plan-monthly discounts when phones are present.
const BUNDLE_DISCOUNTS = [
  { categoryMatch: "tablet",        amount: 40, label: "Phone bundle discount −$40" },
  { categoryMatch: "home internet", amount: 25, label: "Phone bundle discount −$25" },
];
function discountForCategory(name) {
  const n = (name || "").toLowerCase();
  return BUNDLE_DISCOUNTS.find((d) => n.includes(d.categoryMatch));
}

function iconForCategory(name) {
  const n = (name || "").toLowerCase();
  if (n.includes("phone")) return Smartphone;
  if (n.includes("tablet")) return Tablet;
  if (n.includes("home")) return Wifi;
  if (n.includes("hotspot")) return Router;
  return Package;
}
function isPhonesCategory(cat) { return (cat?.name || "").toLowerCase() === "phones"; }
function isTabletCategory(cat) { return (cat?.name || "").toLowerCase().includes("tablet"); }
function isHomeInternetCategory(cat) { return (cat?.name || "").toLowerCase().includes("home"); }
function isHotspotCategory(cat) { return (cat?.name || "").toLowerCase().includes("hotspot"); }
// "BYO Phone" / "BYO Tablet" — new device the customer is bringing. Counts as a real line
// for plan monthly + plan today + access charge, but skips the deal flow (no promo applies).
function isByoDevice(d) {
  if (!d) return false;
  const m = (d.model || "").toLowerCase();
  const b = (d.brand || "").toLowerCase();
  return (m.includes("byo") || b.includes("byo"))
    && !(m.includes("existing") || b.includes("existing"));
}
// "Existing Phone" / "Existing Tablet" / "Existing Home Internet" — placeholder lines the rep
// uses to show add-a-line estimates. They contribute $0 to every total (monthly, today, tax,
// access charge) and don't count toward qualifying-device counts on real promos.
function isExistingDevice(d) {
  if (!d) return false;
  const m = (d.model || "").toLowerCase();
  const b = (d.brand || "").toLowerCase();
  return m.includes("existing") || b.includes("existing");
}
// Either: BYO/Existing devices skip the deal flow.
function isByoOrExistingDevice(d) {
  return isByoDevice(d) || isExistingDevice(d);
}
// "Total MAX BYO" — only available when EVERY phone line is BYO/Existing.
function isByoPlan(p) {
  return /\bbyo\b/i.test(p?.name || "");
}

function tieredPhonesSubtotal(plan, lines) {
  if (!plan || lines <= 0) return 0;
  const tiers = plan.line_tiers || [];
  if (tiers.length > 0) {
    let total = 0;
    for (let i = 1; i <= lines; i++) {
      const tier = tiers.find((t) => i >= t.from_line && i <= t.to_line);
      total += tier ? tier.price : (plan.price_one_line || 0);
    }
    return total;
  }
  return (plan.price_one_line || 0) * lines;
}

function blankPhoneLine() { return { deviceId: null, porting: null, fromCarrierId: "", hasId: null, wantsFinance: null, financeTier: 1 }; }
function blankSimpleLine() { return { deviceId: null, variant: null }; }
function blankHotspotLine() { return { deviceId: null }; }
function blankLineFor(cat) {
  if (isPhonesCategory(cat)) return blankPhoneLine();
  if (isTabletCategory(cat) || isHomeInternetCategory(cat)) return blankSimpleLine();
  return blankHotspotLine();
}

export default function CombinedWizard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);

  // catalog
  const [devices, setDevices] = useState([]);
  const [plans, setPlans] = useState([]);
  const [promos, setPromos] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState({ device_tax_rate_pct: 8.5, device_activation_fee: 35, access_charge_single_line: 25, access_charge_multi_line: 50, tablet_bundle_cap: 2, home_internet_bundle_cap: 1, max_lines_per_account: 10, phone_id_verify_cap: 4 });
  const [addOns, setAddOns] = useState([]);

  // wizard state
  const [mix, setMix] = useState({});                  // { catId: lineCount }
  const [lineStates, setLineStates] = useState({});    // { catId: [line, line, ...] }
  const [phonePlanId, setPhonePlanId] = useState(null);// shared across all phone lines
  const [linePlans, setLinePlans] = useState({});      // { catId: [planId, ...] } non-phone
  const [accountAddOns, setAccountAddOns] = useState(new Set());
  const [lineAddOns, setLineAddOns] = useState({});
  // Customer-entered sales tax % (overrides settings.device_tax_rate_pct). Null until first edit.
  // Seeded automatically from the logged-in rep's saved `default_sales_tax_pct` so they don't
  // have to re-enter the same number on every bundle.
  const [customTaxRate, setCustomTaxRate] = useState(null);
  // Track whether we've already pulled the rep's saved default into the wizard. Prevents
  // wiping a manual edit if the user object refreshes mid-flow.
  const userTaxRateApplied = useRef(false);
  // 'msrp' → sales tax computed on the device's MSRP. 'retail' → on the actual promo/sale price.
  // Default 'msrp' matches most state laws.
  const [taxBasis, setTaxBasis] = useState("msrp");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [d, p, c, cr, pr, s, a] = await Promise.all([
          api.get("/devices", { params: { active_only: true } }),
          api.get("/plans", { params: { active_only: true } }),
          api.get("/categories"),
          api.get("/carriers", { params: { active_only: true } }),
          api.get("/promos", { params: { active_only: true } }),
          api.get("/settings"),
          api.get("/addons", { params: { active_only: true } }),
        ]);
        setDevices(d.data || []);
        setPlans(p.data || []);
        setCategories(c.data || []);
        setCarriers(cr.data || []);
        setPromos(pr.data || []);
        if (s.data) setSettings(s.data);
        setAddOns(a.data || []);
      } finally { setLoading(false); }
    })();
  }, []);

  // Seed the wizard's tax-rate input with the rep's saved default (set during onboarding /
  // profile). Runs once per session — manual edits in step 4 are preserved on subsequent
  // user-context refreshes.
  useEffect(() => {
    if (userTaxRateApplied.current) return;
    if (!user) return;
    const saved = user.default_sales_tax_pct;
    if (saved != null && !Number.isNaN(saved)) {
      setCustomTaxRate(saved);
      userTaxRateApplied.current = true;
    }
  }, [user]);

  const plansByCategory = useMemo(() => {
    const m = {};
    for (const p of plans) {
      const cid = p.category_id || "_uncategorized";
      if (!m[cid]) m[cid] = [];
      m[cid].push(p);
    }
    return m;
  }, [plans]);

  const usableCategories = useMemo(
    () => (categories || []).filter((c) => (plansByCategory[c.id] || []).length > 0),
    [categories, plansByCategory]
  );
  const phonesCat = useMemo(() => usableCategories.find(isPhonesCategory), [usableCategories]);
  const tabletCat = useMemo(() => usableCategories.find(isTabletCategory), [usableCategories]);

  const totalLines = useMemo(() => Object.values(mix).reduce((s, n) => s + (n || 0), 0), [mix]);
  const phoneCount = phonesCat ? (mix[phonesCat.id] || 0) : 0;
  const hasPhones = phoneCount > 0;
  const tabletCount = tabletCat ? (mix[tabletCat.id] || 0) : 0;

  // Counts excluding "Existing" placeholder devices — used by promo qualification
  // (existing lines should not count as "devices the customer is paying for").
  const realPhoneCount = useMemo(() => {
    if (!phonesCat) return 0;
    const arr = lineStates[phonesCat.id] || [];
    return arr.reduce((s, ls) => {
      const dev = devices.find((d) => d.id === ls?.deviceId);
      return s + (isExistingDevice(dev) ? 0 : 1);
    }, 0);
  }, [phonesCat, lineStates, devices]);
  const realTabletCount = useMemo(() => {
    if (!tabletCat) return 0;
    const arr = lineStates[tabletCat.id] || [];
    return arr.reduce((s, ls) => {
      const dev = devices.find((d) => d.id === ls?.deviceId);
      return s + (isExistingDevice(dev) ? 0 : 1);
    }, 0);
  }, [tabletCat, lineStates, devices]);

  // Devices grouped by category, with usable filter per category.
  const devicesInCategory = (catId) => devices.filter((d) => d.category_id === catId);
  const pricingForPhone = (ph) => {
    if (!ph) return null;
    const fromPromo = promos.find((pr) => pr.device_id === ph.id && pr.phone_pricing)?.phone_pricing;
    return fromPromo || ph.phone_pricing || null;
  };
  const usablePhones = useMemo(
    () => devicesInCategory(phonesCat?.id || "__none")
      .filter((p) => isByoOrExistingDevice(p) || hasAnyEnabled(pricingForPhone(p))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [devices, promos, phonesCat]
  );
  const usableTabletDevices = useMemo(() => {
    const cat = usableCategories.find(isTabletCategory);
    if (!cat) return [];
    return devicesInCategory(cat.id)
      .filter((d) => isByoOrExistingDevice(d) || tabletDeviceUsable(d, promos));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices, promos, usableCategories]);
  const usableHIDevices = useMemo(() => {
    const cat = usableCategories.find(isHomeInternetCategory);
    if (!cat) return [];
    return devicesInCategory(cat.id)
      .filter((d) => isByoOrExistingDevice(d) || homeInternetDeviceUsable(d, promos));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices, promos, usableCategories]);
  const usableHotspotDevices = useMemo(() => {
    const cat = usableCategories.find(isHotspotCategory);
    if (!cat) return [];
    return devicesInCategory(cat.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices, usableCategories]);

  const carrierMap = useMemo(() => Object.fromEntries(carriers.map((c) => [c.id, c])), [carriers]);

  // Resize lineStates whenever mix changes
  useEffect(() => {
    setLineStates((prev) => {
      const next = { ...prev };
      for (const cat of usableCategories) {
        const desired = mix[cat.id] || 0;
        const cur = next[cat.id] || [];
        if (cur.length === desired) continue;
        if (cur.length > desired) {
          next[cat.id] = cur.slice(0, desired);
        } else {
          const extra = Array.from({ length: desired - cur.length }, () => blankLineFor(cat));
          next[cat.id] = [...cur, ...extra];
        }
      }
      // Also remove categories no longer in mix
      for (const k of Object.keys(next)) {
        if (!(k in mix) || (mix[k] || 0) === 0) {
          delete next[k];
        }
      }
      return next;
    });
  }, [mix, usableCategories]);

  const setCategoryLines = (catId, n) => {
    const maxLines = settings.max_lines_per_account ?? DEFAULT_MAX_LINES;
    const next = Math.max(0, Math.min(maxLines, n));
    const otherTotal = Object.entries(mix).reduce((s, [k, v]) => s + (k === catId ? 0 : (v || 0)), 0);
    const clamped = Math.min(next, maxLines - otherTotal);
    setMix((m) => ({ ...m, [catId]: clamped }));
    if (catId !== phonesCat?.id) {
      setLinePlans((lp) => {
        const cur = lp[catId] || [];
        const trimmed = cur.slice(0, clamped);
        while (trimmed.length < clamped) trimmed.push(null);
        return { ...lp, [catId]: trimmed };
      });
    }
  };

  const updateLine = (catId, idx, patch) => {
    setLineStates((prev) => {
      const arr = (prev[catId] || []).slice();
      arr[idx] = { ...(arr[idx] || {}), ...patch };
      return { ...prev, [catId]: arr };
    });
  };

  // ---- Per-line evaluation (phones / tablets / HI) ----------------------

  /** Evaluate a single phone line: returns {options, filteredOptions, recommended, activeTier, ready} */
  // Phone line evaluator.
  //   `beyondIdCap` — true when earlier phone lines on this account have already filled the
  //                   admin-configurable ID-verify cap (default 4). The caller is responsible for
  //                   tallying which previous lines consumed an ID-verify slot (finance lines,
  //                   BYO and Existing lines do NOT consume a slot).
  //   `financeCapReached` — true when earlier phone lines already hit the finance-device cap.
  const evalPhoneLine = (state, beyondIdCap, financeCapReached = false) => {
    const device = devices.find((d) => d.id === state?.deviceId);
    // BYO / Existing devices skip the deal flow entirely. They contribute only their plan.
    if (device && isByoOrExistingDevice(device)) {
      return {
        device, pricing: null, options: [], financeAvailable: false,
        filtered: [], qualifications: null, recommended: null, activeTier: null,
        ready: true, beyondIdCap: false, financeCapReached: false,
        isByo: isByoDevice(device),
        isExisting: isExistingDevice(device),
      };
    }
    const pricing = pricingForPhone(device);
    const options = enumerateOptions(pricing);
    // ID verify cap: once the customer has used up the cap of ID-verified phone lines, ALL
    // subsequent ID-verifying lines collapse to either No Hassle (when port-in or new number) or
    // Full Retail. Finance, BYO, and Existing lines do NOT count toward the cap, so the caller
    // pre-computes `beyondIdCap` by tallying only ID-consuming previous lines.
    const idCap = settings.phone_id_verify_cap ?? 4;
    void idCap; // cap value is informational here; `beyondIdCap` is computed by the caller.
    // Finance-device cap: once N earlier phone lines on this account have already picked Finance,
    // remove the Finance scenario from THIS line's options. The "Qualify for financing?" question
    // disappears (financeAvailable=false) and the auto-recommend logic falls through to Switch/etc.
    let scoped = beyondIdCap
      ? options.filter((o) => o.group === "no_hassle" || o.group === "full_retail")
      : options;
    if (financeCapReached) scoped = scoped.filter((o) => !o.isFinance);
    const financeAvailable = !!scoped.find((o) => o.isFinance);
    // Filter the scenario list based on the finance question:
    //   wantsFinance === true  → only finance options (skip Switch/Join/No-Hassle/Full-Retail)
    //   wantsFinance === false → drop finance entirely (use traditional promos)
    //   null/undefined         → keep everything (we haven't asked yet, or finance isn't available)
    let filtered = scoped;
    if (state?.wantsFinance === true) filtered = scoped.filter((o) => o.isFinance);
    else if (state?.wantsFinance === false) filtered = scoped.filter((o) => !o.isFinance);
    let qualifications = null;
    // Readiness gate:
    //   • If the user chose Finance, we don't need ID or port-in answers — finance qualifies on its own.
    //   • Otherwise (or when finance isn't available), require ID + port-in answers (+ carrier when porting).
    const financeReady = financeAvailable && state?.wantsFinance === true;
    const traditionalReady = state?.hasId !== null && state?.porting !== null
      && (!financeAvailable || state?.wantsFinance !== null)
      && (state?.porting === false || state?.fromCarrierId);
    if (financeReady || traditionalReady) {
      const fromCarrier = carriers.find((c) => c.id === state.fromCarrierId);
      const answers = {
        hasId: state.hasId, porting: state.porting, fromCarrierId: state.fromCarrierId,
        fromCarrierEligible: fromCarrier ? !!fromCarrier.is_port_in_eligible : undefined,
        planId: null,
        lines: realPhoneCount,
        qualifyingDeviceCount: realPhoneCount + realTabletCount,
      };
      qualifications = filtered.map((opt) => ({ ...opt, ...evaluateOption(opt, answers) }));
    }
    const recommended = qualifications
      ?.filter((q) => q.ok)
      .slice()
      .sort((a, b) => {
        // 1. Group priority: Switch > Join > No Hassle (matches dealer funnel).
        if ((b.groupPriority || 0) !== (a.groupPriority || 0)) return (b.groupPriority || 0) - (a.groupPriority || 0);
        // 2. Sub-variant priority within group: multi_line > multi_month > single_month.
        if ((b.priority || 0) !== (a.priority || 0)) return (b.priority || 0) - (a.priority || 0);
        // 3. Tiebreaker: cheaper headline price wins.
        return a.priceForRecommend - b.priceForRecommend;
      })[0];
    const tiers = recommended?.financeTiers || [];
    const activeTier = recommended?.isFinance
      ? (tiers.find((t) => t.tier === (state?.financeTier || 1)) || tiers[0])
      : null;
    const ready = !!(device && recommended);
    return { device, pricing, options, financeAvailable, filtered, qualifications, recommended, activeTier, ready, beyondIdCap, financeCapReached };
  };

  /** Active tablet variant for a line index — also honours the bundle cap. */
  const evalTabletLine = (state, lineIdx) => {
    const device = devices.find((d) => d.id === state?.deviceId);
    if (device && isByoOrExistingDevice(device)) {
      return { device, pricing: null, variants: [], chosen: null, ready: true, isByo: isByoDevice(device), isExisting: isExistingDevice(device) };
    }
    const pricing = tabletPricingFor(device, promos);
    // Determine if THIS index is still within the bundle cap.
    // Order: by lineIdx. The first N tablet lines may use bundle (admin-configurable cap).
    const withinBundleCap = lineIdx < (settings.tablet_bundle_cap ?? 2);
    const variants = tabletVariants(pricing, hasPhones, withinBundleCap);
    const chosen = variants.find((v) => v.key === state?.variant && !v.disabled);
    const ready = !!(device && chosen);
    return { device, pricing, variants, chosen, ready };
  };

  /** Active HI variant for a line index — honours the 1-line bundle cap. */
  const evalHILine = (state, lineIdx) => {
    const device = devices.find((d) => d.id === state?.deviceId);
    if (device && isByoOrExistingDevice(device)) {
      return { device, pricing: null, variants: [], chosen: null, ready: true, isByo: isByoDevice(device), isExisting: isExistingDevice(device) };
    }
    const pricing = homeInternetPricingFor(device, promos);
    const withinBundleCap = lineIdx < (settings.home_internet_bundle_cap ?? 1);
    const variants = homeInternetVariants(pricing, hasPhones, withinBundleCap);
    const chosen = variants.find((v) => v.key === state?.variant && !v.disabled);
    const ready = !!(device && chosen);
    return { device, pricing, variants, chosen, ready };
  };

  const evalHotspotLine = (state) => {
    const device = devices.find((d) => d.id === state?.deviceId);
    // Pull a hotspot_pricing promo (if any) for this device.
    const promo = promos.find((pr) => pr.device_id === device?.id && pr.hotspot_pricing?.enabled);
    const promoPrice = promo?.hotspot_pricing?.price || 0;
    const ready = !!device;
    return { device, promo, promoPrice, ready };
  };

  // Per-line evaluations (memoised list of {catId, idx, kind, eval})
  const allLineEvals = useMemo(() => {
    const out = [];
    const financeCap = settings.allowed_finance_devices_per_account ?? 1;
    const idCap = settings.phone_id_verify_cap ?? 4;
    let financeUsed = 0;
    // Tally of phone lines that have ALREADY consumed an ID-verify slot. A line consumes a slot
    // only when it picks a real (non-BYO / non-Existing) device AND the customer is NOT financing
    // it. Lines without a chosen device are pending and don't consume the slot yet.
    let idVerifyUsed = 0;
    for (const cat of usableCategories) {
      const n = mix[cat.id] || 0;
      if (n === 0) continue;
      const arr = lineStates[cat.id] || [];
      for (let i = 0; i < n; i++) {
        const ls = arr[i];
        let kind = "hotspot";
        let evalObj;
        if (isPhonesCategory(cat)) {
          kind = "phone";
          const financeCapReached = financeUsed >= financeCap;
          const beyondIdCap = idVerifyUsed >= idCap;
          evalObj = evalPhoneLine(ls, beyondIdCap, financeCapReached);
          // Tally finance + id-verify usage for downstream lines.
          const isFinance = evalObj.recommended?.isFinance || ls?.wantsFinance === true;
          if (isFinance) financeUsed += 1;
          // An ID-verify slot is only consumed once the rep has actually answered "Yes" to the
          // Valid-government-ID? question. Picking a device alone (or saying No to ID) does NOT
          // burn a slot. BYO, Existing, and Finance lines never consume a slot.
          const consumesIdSlot = !!evalObj.device
            && !evalObj.isByo
            && !evalObj.isExisting
            && !isFinance
            && ls?.hasId === true;
          if (consumesIdSlot) idVerifyUsed += 1;
        }
        else if (isTabletCategory(cat)) { kind = "tablet"; evalObj = evalTabletLine(ls, i); }
        else if (isHomeInternetCategory(cat)) { kind = "hi"; evalObj = evalHILine(ls, i); }
        else { kind = "hotspot"; evalObj = evalHotspotLine(ls); }
        out.push({ cat, idx: i, kind, state: ls, eval: evalObj });
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usableCategories, mix, lineStates, devices, promos, phoneCount, tabletCount, hasPhones, settings.allowed_finance_devices_per_account, settings.phone_id_verify_cap]);

  // Slot-usage stats surfaced in the Bundle Summary so reps can see at a glance when they're
  // about to hit the ID-verify cap. Re-uses the same "consumes a slot" rule as the evaluator
  // above: a slot is only burned once the rep answers "Yes" to the Valid-government-ID? question
  // on a non-Finance / non-BYO / non-Existing phone line.
  const idCapStats = useMemo(() => {
    const cap = settings.phone_id_verify_cap ?? 4;
    let used = 0;
    for (const L of allLineEvals) {
      if (L.kind !== "phone") continue;
      const e = L.eval;
      const isFinance = e.recommended?.isFinance || L.state?.wantsFinance === true;
      if (e.device && !e.isByo && !e.isExisting && !isFinance && L.state?.hasId === true) used += 1;
    }
    return { used, cap, remaining: Math.max(0, cap - used), atCap: used >= cap };
  }, [allLineEvals, settings.phone_id_verify_cap]);

  // Effective sales-tax rate (%): the customer's override if they entered one on step 4,
  // otherwise the admin's default from /api/settings.
  const effectiveTaxRatePct = (customTaxRate != null && !Number.isNaN(customTaxRate))
    ? customTaxRate
    : (settings.device_tax_rate_pct || 0);

  // Compute aggregate device totals (no activation fees baked in here — those are replaced
  // by a single account-level "access charge" tied to the phone count).
  const deviceTotals = useMemo(() => {
    const taxRate = (effectiveTaxRatePct || 0) / 100;
    // Pick the tax basis: 'msrp' = device.msrp, 'retail' = the actual upfront price for the line.
    const basisFor = (device, retailPrice) => {
      if (taxBasis === "msrp") return device?.msrp || 0;
      return retailPrice || 0;
    };
    let upfront = 0, tax = 0, financeMonthly = 0;
    for (const L of allLineEvals) {
      if (L.kind === "phone") {
        const rec = L.eval.recommended;
        if (!rec) continue;
        if (rec.isFinance) {
          const t = L.eval.activeTier;
          if (t) {
            tax += basisFor(L.eval.device, t.devicePrice || 0) * taxRate;
            financeMonthly += t.price || 0;
          }
        } else {
          const price = rec.priceForRecommend || 0;
          upfront += price;
          tax += basisFor(L.eval.device, price) * taxRate;
        }
      } else if (L.kind === "tablet" || L.kind === "hi") {
        const v = L.eval.chosen;
        if (!v) continue;
        upfront += v.price || 0;
        tax += basisFor(L.eval.device, v.price || 0) * taxRate;
      } else if (L.kind === "hotspot") {
        const hp = L.eval.promoPrice || 0;
        if (hp > 0) {
          upfront += hp;
          tax += basisFor(L.eval.device, hp) * taxRate;
        }
      }
      // hotspot without a hotspot_pricing promo: no device pricing (reference only)
    }
    // Access charge — phones-only, account-level. Existing phone lines are placeholders and
    // don't count toward the access-charge funnel.
    let accessCharge = 0;
    if (realPhoneCount === 1) accessCharge = settings.access_charge_single_line || 0;
    else if (realPhoneCount >= 2) accessCharge = settings.access_charge_multi_line || 0;
    const total = upfront + tax + accessCharge;
    return { upfront, tax, accessCharge, total, financeMonthly };
  }, [allLineEvals, effectiveTaxRatePct, taxBasis, settings.access_charge_single_line, settings.access_charge_multi_line, realPhoneCount]);

  // Phone-plan eligibility = intersection of every NON-BYO phone line's recommended eligible_plan_ids.
  // BYO phone lines have no promo and don't constrain the plan list.
  // If ANY phone line is non-BYO, Total MAX BYO (or any plan flagged BYO) is excluded.
  const phoneEligiblePlans = useMemo(() => {
    const all = phonesCat ? (plansByCategory[phonesCat.id] || []) : [];
    if (!hasPhones) return all;
    const phoneLines = allLineEvals.filter((L) => L.kind === "phone");
    const nonByoLines = phoneLines.filter((L) => !L.eval.isByo);
    const everyLineIsByo = phoneLines.length > 0 && nonByoLines.length === 0;
    // All-BYO bundle → any plan (including MAX BYO) is allowed.
    if (everyLineIsByo) return all;
    // Mixed or all-non-BYO → wait until every non-BYO line has a recommendation.
    const ready = nonByoLines.every((L) => L.eval.recommended);
    if (!ready) return all.filter((p) => !isByoPlan(p));
    let pool = all.slice();
    for (const L of nonByoLines) {
      const elig = L.eval.recommended?.data?.eligible_plan_ids || [];
      if (elig.length === 0) continue;
      pool = pool.filter((p) => elig.includes(p.id));
    }
    // Strip MAX BYO since the bundle has at least one non-BYO line.
    return pool.filter((p) => !isByoPlan(p));
  }, [phonesCat, plansByCategory, hasPhones, allLineEvals]);

  useEffect(() => {
    if (phoneEligiblePlans.length === 1 && phonePlanId !== phoneEligiblePlans[0].id) {
      setPhonePlanId(phoneEligiblePlans[0].id);
    } else if (phonePlanId && phoneEligiblePlans.length > 0 && !phoneEligiblePlans.find((p) => p.id === phonePlanId)) {
      setPhonePlanId(null);
    }
  }, [phoneEligiblePlans, phonePlanId]);

  // Per-line eligible plans for tablet/HI (filtered by chosen variant's eligible_plan_ids when set)
  const eligiblePlansForLine = (L) => {
    const all = plansByCategory[L.cat.id] || [];
    if (L.kind === "tablet" || L.kind === "hi") {
      const ids = L.eval.chosen?.eligible_plan_ids || [];
      if (ids.length === 0) return all;
      return all.filter((p) => ids.includes(p.id));
    }
    return all;
  };

  // Build bill rows (per line)
  const phonePlan = plans.find((p) => p.id === phonePlanId);
  const categoryGroups = useMemo(() => {
    return usableCategories
      .map((cat) => ({ cat, lines: mix[cat.id] || 0 }))
      .filter((g) => g.lines > 0);
  }, [usableCategories, mix]);

  const billRows = useMemo(() => {
    const rows = [];
    const taxRate = (effectiveTaxRatePct || 0) / 100;
    const basisFor = (device, retailPrice) => {
      if (taxBasis === "msrp") return device?.msrp || 0;
      return retailPrice || 0;
    };
    for (const g of categoryGroups) {
      const bundle = !isPhonesCategory(g.cat) && hasPhones ? discountForCategory(g.cat.name) : null;
      if (isPhonesCategory(g.cat)) {
        if (!phonePlan) continue;
        const tiers = phonePlan.line_tiers || [];
        for (let i = 1; i <= g.lines; i++) {
          const tier = tiers.find((t) => i >= t.from_line && i <= t.to_line);
          const tierPrice = tier ? tier.price : (phonePlan.price_one_line || 0);
          const L = allLineEvals.find((x) => x.cat.id === g.cat.id && x.idx === i - 1);
          const dev = L?.eval.device;
          const isExisting = !!L?.eval.isExisting;
          // Existing lines ARE billed monthly (placeholder so the rep can show the customer
          // their full monthly bill once the add-a-lines are activated) — but they DON'T
          // contribute to today's upfront cost or the access charge (handled elsewhere).
          const linePrice = tierPrice;
          const rec = L?.eval.recommended;
          const at = L?.eval.activeTier;
          // device_one_time_price = device price + sales tax ONLY (no activation here;
          // access charge is account-level and stored separately on the quote).
          // Tax is computed on the MSRP or retail price per the wizard's tax basis toggle.
          // device_subtotal / device_tax persist the breakdown so the receipt's
          // "Today's charges" can show device price and sales tax as separate lines.
          let devSubtotal = null, devTax = null, devOneTime = null;
          if (rec) {
            if (rec.isFinance) {
              devSubtotal = 0;
              devTax = basisFor(dev, at?.devicePrice || 0) * taxRate;
            } else {
              devSubtotal = rec.priceForRecommend || 0;
              devTax = basisFor(dev, devSubtotal) * taxRate;
            }
            devOneTime = devSubtotal + devTax;
          }
          rows.push({
            category_id: g.cat.id, category_name: g.cat.name,
            plan_id: phonePlan.id, plan_name: phonePlan.name,
            line_number: i, line_price: linePrice,
            original_price: null,
            discount_label: isExisting ? "Existing line — monthly only (no upfront)" : null,
            device_id: dev?.id || null,
            device_name: dev ? `${dev.brand} ${dev.model}` : null,
            device_scenario_key: rec?.key || null,
            device_scenario_label: rec?.label || null,
            device_one_time_price: devOneTime,
            device_subtotal: devSubtotal,
            device_tax: devTax,
            device_monthly_price: at?.price || 0,
          });
        }
      } else {
        const arr = linePlans[g.cat.id] || [];
        for (let i = 0; i < g.lines; i++) {
          const pid = arr[i];
          const pl = plans.find((p) => p.id === pid);
          if (!pl) continue;
          const base = pl.price_one_line || 0;
          const discounted = bundle ? Math.max(0, base - bundle.amount) : base;
          const L = allLineEvals.find((x) => x.cat.id === g.cat.id && x.idx === i);
          const dev = L?.eval.device;
          const isExisting = !!L?.eval.isExisting;
          let devOneTime = null;
          let devSubtotal = null, devTax = null;
          let scenKey = null, scenLabel = null;
          if (L && (L.kind === "tablet" || L.kind === "hi")) {
            const v = L.eval.chosen;
            if (v) {
              devSubtotal = v.price || 0;
              devTax = basisFor(dev, devSubtotal) * taxRate;
              devOneTime = devSubtotal + devTax;
              scenKey = v.key; scenLabel = v.label;
            }
          } else if (L && L.kind === "hotspot") {
            const hp = L.eval.promoPrice || 0;
            if (hp > 0) {
              devSubtotal = hp;
              devTax = basisFor(dev, hp) * taxRate;
              devOneTime = devSubtotal + devTax;
              scenKey = "hotspot_promo"; scenLabel = "Promo";
            }
          }
          rows.push({
            category_id: g.cat.id, category_name: g.cat.name,
            plan_id: pl.id, plan_name: pl.name,
            line_number: i + 1, line_price: discounted,
            original_price: bundle ? base : null,
            discount_label: isExisting
              ? "Existing line — monthly only (no upfront)"
              : (bundle ? bundle.label : null),
            device_id: dev?.id || null,
            device_name: dev ? `${dev.brand} ${dev.model}` : null,
            device_scenario_key: scenKey,
            device_scenario_label: scenLabel,
            device_one_time_price: devOneTime,
            device_subtotal: devSubtotal,
            device_tax: devTax,
            device_monthly_price: null,
          });
        }
      }
    }
    return rows;
  }, [categoryGroups, phonePlan, linePlans, plans, hasPhones, allLineEvals, effectiveTaxRatePct, taxBasis]);

  const planMonthly = useMemo(() => billRows.reduce((s, r) => s + r.line_price, 0), [billRows]);

  // "Plan today" — exactly ONE month of every REAL line's plan is due today.
  //   - Existing/placeholder lines contribute $0 (they're add-a-line previews).
  //   - The multi-month variant on a promo is a *commitment*, not a prepay — only the
  //     first month is collected today regardless of required_months.
  //   - A line contributes $0 only when no device has been picked yet.
  const planToday = useMemo(() => {
    let total = 0;
    for (const L of allLineEvals) {
      if (!L.eval.device) continue; // no device yet
      if (L.eval.isExisting) continue; // existing/placeholder lines never contribute to today
      if (L.kind === "phone") {
        if (!L.eval.isByo && !L.eval.recommended) continue; // promo not picked yet
      } else if (L.kind === "tablet" || L.kind === "hi") {
        if (!L.eval.isByo && !L.eval.chosen) continue; // variant not picked yet
      }
      const row = billRows.find((r) => r.category_id === L.cat.id && r.line_number === L.idx + 1);
      if (!row) continue;
      total += (row.line_price || 0);
    }
    return total;
  }, [allLineEvals, billRows]);

  // ---- Add-ons -----------------------------------------------------------
  const isAutoPayAddOn = (a) => /auto\s*pay/i.test(a.name || "");
  const accountWideAddOns = useMemo(
    () => addOns.filter((a) => (a.applies_to_categories || []).length === 0 || isAutoPayAddOn(a)),
    [addOns]
  );
  const lineLevelAddOnsFor = (catId) => addOns.filter((a) => !isAutoPayAddOn(a) && (a.applies_to_categories || []).includes(catId));
  const anyAddOnsAvailable = addOns.length > 0;

  const addOnLines = useMemo(() => {
    const cappedPrice = (a) => {
      if (isAutoPayAddOn(a) && (a.monthly_price || 0) < 0) return Math.max(-5, a.monthly_price);
      return a.monthly_price;
    };
    const out = [];
    for (const a of addOns) {
      const cft = !!a.charge_first_month_today;
      const otp = a.one_time_price || 0; // per-occurrence one-time charge
      // Auto Pay is ALWAYS treated as account-wide (matches the UI), regardless of
      // whatever applies_to_categories the admin set.
      if ((a.applies_to_categories || []).length === 0 || isAutoPayAddOn(a)) {
        if (accountAddOns && accountAddOns.has && accountAddOns.has(a.id)) {
          out.push({ name: a.name, price: cappedPrice(a), one_time_price: otp, count: 1, charge_first_month_today: cft });
        }
      } else {
        let occurrences = 0;
        for (const catId of Object.keys(lineAddOns || {})) {
          const m = lineAddOns[catId] || {};
          for (const idx of Object.keys(m)) {
            const set = m[idx];
            if (set && typeof set.has === "function" && set.has(a.id)) occurrences += 1;
          }
        }
        if (occurrences > 0) {
          const baseName = occurrences === 1 ? a.name : `${a.name} × ${occurrences}`;
          out.push({ name: baseName, price: a.monthly_price * occurrences, one_time_price: otp * occurrences, count: occurrences, charge_first_month_today: cft });
        }
      }
    }
    return out;
  }, [addOns, accountAddOns, lineAddOns]);
  // Monthly total excludes today-only add-ons (those with no monthly_price contribute 0 here naturally).
  const addOnsTotal = addOnLines.reduce((s, l) => s + (l.price || 0), 0);
  // Today's add-on charges = first-month-today (TWP+ style) PLUS explicit one_time_price (SIM kit style).
  const addOnsToday = addOnLines.reduce((s, l) => {
    const firstMonth = l.charge_first_month_today && (l.price || 0) > 0 ? l.price : 0;
    return s + firstMonth + (l.one_time_price || 0);
  }, 0);
  // Add-ons that contribute to today only (no monthly price) — surfaced separately in the summary.
  const todayOnlyAddOnRows = addOnLines.filter((l) => (l.one_time_price || 0) > 0);

  const toggleAccountAddOn = (id) => {
    setAccountAddOns((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };
  const toggleLineAddOn = (catId, idx, addonId) => {
    setLineAddOns((prev) => {
      const catMap = { ...(prev[catId] || {}) };
      const cur = catMap[idx] instanceof Set ? new Set(catMap[idx]) : new Set();
      if (cur.has(addonId)) cur.delete(addonId); else cur.add(addonId);
      catMap[idx] = cur;
      return { ...prev, [catId]: catMap };
    });
  };

  // ---- Steps -------------------------------------------------------------
  const stepDefs = useMemo(() => {
    const list = [{ key: "mix", title: "01 — How many of each?" }];
    list.push({ key: "devices", title: `0${list.length + 1} — Devices & deals (per line)` });
    list.push({ key: "plans", title: `0${list.length + 1} — Pick plans` });
    if (anyAddOnsAvailable) list.push({ key: "addons", title: `0${list.length + 1} — Add-ons` });
    list.push({ key: "customer", title: `0${list.length + 1} — Customer info` });
    return list;
  }, [anyAddOnsAvailable]);
  const currentStep = stepDefs[step - 1];

  const devicesStepReady = totalLines > 0 && allLineEvals.every((L) => L.eval.ready);

  const plansReady = (() => {
    if (hasPhones && !phonePlanId) return false;
    for (const g of categoryGroups) {
      if (isPhonesCategory(g.cat)) continue;
      const arr = linePlans[g.cat.id] || [];
      for (let i = 0; i < g.lines; i++) if (!arr[i]) return false;
    }
    return true;
  })();
  const canNext = () => {
    const k = currentStep?.key;
    if (k === "mix") return totalLines > 0;
    if (k === "devices") return devicesStepReady;
    if (k === "plans") return plansReady;
    return true;
  };

  useEffect(() => { if (step > stepDefs.length) setStep(stepDefs.length); }, [stepDefs.length, step]);

  const save = async () => {
    if (billRows.length === 0) return;
    setSaving(true);
    try {
      const primary = billRows[0];
      const planNames = Array.from(new Set(billRows.map((r) => r.plan_name)));
      const summaryName = planNames.length === 1 ? planNames[0] : `Bundle (${planNames.length} plans)`;
      // First phone line (legacy top-level device fields)
      const firstPhone = billRows.find((r) => r.category_id === phonesCat?.id && r.device_id);
      const payload = {
        plan_id: primary.plan_id,
        plan_name: summaryName,
        lines: totalLines,
        base_price: primary.line_price,
        addons: addOnLines.map((l) => ({ name: l.name, price: round2(l.price), one_time_price: round2(l.one_time_price || 0), charge_first_month_today: !!l.charge_first_month_today })),
        subtotal: round2(planMonthly),
        taxes_fees: round2(addOnsTotal),
        total: round2(planMonthly + addOnsTotal),
        customer_name: name || null,
        customer_email: email || null,
        customer_phone: phone || null,
        consent_to_contact: !!consent,
        bill_items: billRows.map((r) => ({
          category_id: r.category_id, category_name: r.category_name,
          plan_id: r.plan_id, plan_name: r.plan_name,
          line_number: r.line_number, line_price: round2(r.line_price),
          original_price: r.original_price != null ? round2(r.original_price) : null,
          discount_label: r.discount_label || null,
          device_id: r.device_id || null,
          device_name: r.device_name || null,
          device_scenario_key: r.device_scenario_key || null,
          device_scenario_label: r.device_scenario_label || null,
          device_one_time_price: r.device_one_time_price != null ? round2(r.device_one_time_price) : null,
          device_subtotal: r.device_subtotal != null ? round2(r.device_subtotal) : null,
          device_tax: r.device_tax != null ? round2(r.device_tax) : null,
          device_monthly_price: r.device_monthly_price != null ? round2(r.device_monthly_price) : null,
        })),
        device_id: firstPhone?.device_id || null,
        device_name: firstPhone?.device_name || null,
        device_scenario_key: firstPhone?.device_scenario_key || null,
        device_one_time_price: firstPhone?.device_one_time_price != null ? round2(firstPhone.device_one_time_price) : null,
        device_monthly_price: firstPhone?.device_monthly_price != null ? round2(firstPhone.device_monthly_price) : null,
        access_charge: deviceTotals.accessCharge > 0 ? round2(deviceTotals.accessCharge) : null,
        plan_today: planToday > 0 ? round2(planToday) : null,
      };
      const { data } = await api.post("/quotes", payload);
      toast.success("Bundle quote saved!");
      navigate(`/quote/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <main className="bg-slate-50 min-h-[80vh]" data-testid="page-combined-wizard">
      <div className="tw-container py-12 lg:py-16">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div className="tw-overline">Bundle builder</div>
            <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tighter text-[#0B132B]">
              Mix, match <span className="text-[#00E5FF]">+</span> save
            </h1>
            <p className="mt-3 text-slate-600 max-w-xl">
              Pick lines per device type, then pick a device + deal for each line. {settings.max_lines_per_account ?? DEFAULT_MAX_LINES}-line cap.
            </p>
          </div>
          <StepIndicator step={step} count={stepDefs.length} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 bg-white border border-slate-200">
            <AnimatePresence mode="wait">
              {currentStep?.key === "mix" && (
                <StepWrap key="mix" title={currentStep.title}>
                  {loading ? <CenteredLoader /> : usableCategories.length === 0 ? (
                    <EmptyNotice title="No plans configured" body="Add plans in at least one category to get started." />
                  ) : (
                    <div className="space-y-3" data-testid="cw-mix">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm text-slate-600">Mix and match phones, tablets, home internet, hotspots.</p>
                        <span className={`badge-pill ${totalLines >= (settings.max_lines_per_account ?? DEFAULT_MAX_LINES) ? "badge-amber" : "badge-outline"}`} data-testid="cw-line-cap-badge">
                          {totalLines} / {settings.max_lines_per_account ?? DEFAULT_MAX_LINES} lines
                        </span>
                      </div>
                      {usableCategories.map((cat) => {
                        const Icon = iconForCategory(cat.name);
                        const n = mix[cat.id] || 0;
                        return (
                          <div key={cat.id} className="flex items-center justify-between gap-4 p-4 border border-slate-200" data-testid={`cw-mix-row-${cat.id}`}>
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="h-10 w-10 bg-[#0B132B] grid place-items-center flex-shrink-0">
                                <Icon className="h-5 w-5 text-[#00E5FF]" />
                              </div>
                              <div>
                                <div className="font-bold text-[#0B132B]">{cat.name}</div>
                                <div className="text-xs text-slate-500 mono">{(plansByCategory[cat.id] || []).length} plan{(plansByCategory[cat.id] || []).length > 1 ? "s" : ""} · {isPhonesCategory(cat) ? "one shared plan" : "each line picks its own"}</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                              <button
                                onClick={() => setCategoryLines(cat.id, n - 1)}
                                disabled={n === 0}
                                className="h-9 w-9 border-2 border-[#0B132B] grid place-items-center hover:bg-[#0B132B] hover:text-white transition disabled:opacity-30 disabled:cursor-not-allowed"
                                data-testid={`cw-mix-dec-${cat.id}`}
                              ><Minus className="h-4 w-4" /></button>
                              <div className="mono text-3xl font-bold text-[#0B132B] w-12 text-center ticker-num" data-testid={`cw-mix-value-${cat.id}`}>{n}</div>
                              <button
                                onClick={() => setCategoryLines(cat.id, n + 1)}
                                disabled={totalLines >= (settings.max_lines_per_account ?? DEFAULT_MAX_LINES)}
                                className="h-9 w-9 border-2 border-[#0B132B] grid place-items-center hover:bg-[#0B132B] hover:text-white transition disabled:opacity-30 disabled:cursor-not-allowed"
                                data-testid={`cw-mix-inc-${cat.id}`}
                              ><Plus className="h-4 w-4" /></button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </StepWrap>
              )}

              {currentStep?.key === "devices" && (
                <StepWrap key="devices" title={currentStep.title}>
                  {allLineEvals.length === 0 ? (
                    <EmptyNotice title="No lines yet" body="Go back to step 1 and pick at least one line." />
                  ) : (
                    <div className="space-y-6" data-testid="cw-devices-step">
                      {allLineEvals.map((L) => (
                        <LineCard
                          key={`${L.cat.id}-${L.idx}`}
                          L={L}
                          carriers={carriers}
                          carrierMap={carrierMap}
                          usablePhones={usablePhones}
                          usableTabletDevices={usableTabletDevices}
                          usableHIDevices={usableHIDevices}
                          usableHotspotDevices={usableHotspotDevices}
                          pricingForPhone={pricingForPhone}
                          onUpdate={(patch) => updateLine(L.cat.id, L.idx, patch)}
                        />
                      ))}
                    </div>
                  )}
                </StepWrap>
              )}

              {currentStep?.key === "plans" && (
                <StepWrap key="plans" title={currentStep.title}>
                  {categoryGroups.length === 0 ? (
                    <EmptyNotice title="Nothing selected" body="Go back to step 1 and pick at least one line." />
                  ) : (
                    <div className="space-y-8">
                      {hasPhones && (
                        <CategorySection
                          icon={iconForCategory("Phones")}
                          title={`Phones · ${phoneCount} line${phoneCount > 1 ? "s" : ""}`}
                          subtitle="All phone lines share one plan. Per-line tier pricing applies."
                          testKey="section-phones"
                        >
                          {phoneEligiblePlans.length < (plansByCategory[phonesCat?.id]?.length || 0) && (
                            <div className="text-xs text-slate-500 mb-2" data-testid="cw-plans-hint">
                              Plans filtered by the phone-deal recommendations across all phone lines.
                            </div>
                          )}
                          {phoneEligiblePlans.length === 0 ? (
                            <EmptyNotice title="No qualifying phone plans" body="No plan is on every phone line's deal-qualifying list." />
                          ) : (
                            <SharedPlanPicker
                              plans={phoneEligiblePlans}
                              selectedId={phonePlanId}
                              onSelect={setPhonePlanId}
                              lines={phoneCount}
                            />
                          )}
                        </CategorySection>
                      )}
                      {categoryGroups.filter((g) => !isPhonesCategory(g.cat)).map((g) => {
                        const bundleDiscount = hasPhones ? discountForCategory(g.cat.name) : null;
                        return (
                          <CategorySection
                            key={g.cat.id}
                            icon={iconForCategory(g.cat.name)}
                            title={`${g.cat.name} · ${g.lines} line${g.lines > 1 ? "s" : ""}`}
                            subtitle="Each line can be a different plan."
                            testKey={`section-${g.cat.id}`}
                          >
                            <PerLinePlanPicker
                              categoryId={g.cat.id}
                              categoryName={g.cat.name}
                              lines={g.lines}
                              selectedIds={linePlans[g.cat.id] || []}
                              onChange={(arr) => setLinePlans((lp) => ({ ...lp, [g.cat.id]: arr }))}
                              bundleDiscount={bundleDiscount}
                              eligibleFor={(idx) => {
                                const L = allLineEvals.find((x) => x.cat.id === g.cat.id && x.idx === idx);
                                return L ? eligiblePlansForLine(L) : (plansByCategory[g.cat.id] || []);
                              }}
                            />
                          </CategorySection>
                        );
                      })}
                    </div>
                  )}
                </StepWrap>
              )}

              {currentStep?.key === "addons" && (
                <StepWrap key="addons" title={currentStep.title}>
                  <AddOnsStep
                    addOns={addOns}
                    categoryGroups={categoryGroups}
                    accountWideAddOns={accountWideAddOns}
                    accountAddOns={accountAddOns}
                    onToggleAccount={toggleAccountAddOn}
                    lineAddOns={lineAddOns}
                    onToggleLine={toggleLineAddOn}
                    lineLevelAddOnsFor={lineLevelAddOnsFor}
                    taxRate={effectiveTaxRatePct}
                    defaultTaxRate={settings.device_tax_rate_pct || 0}
                    onTaxRateChange={setCustomTaxRate}
                    taxBasis={taxBasis}
                    onTaxBasisChange={setTaxBasis}
                  />
                </StepWrap>
              )}

              {currentStep?.key === "customer" && (
                <StepWrap key="customer" title={currentStep.title}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <label className="block">
                      <span className="tw-overline">Name</span>
                      <input value={name} onChange={(e) => setName(e.target.value)} className="input-sharp mt-2" data-testid="cw-name" placeholder="Jane Doe" />
                    </label>
                    <label className="block">
                      <span className="tw-overline">Phone number</span>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="input-sharp mt-2"
                        data-testid="cw-phone"
                        placeholder="(555) 123-4567"
                        autoComplete="tel"
                      />
                    </label>
                    <label className="block">
                      <span className="tw-overline">Email</span>
                      <input value={email} onChange={(e) => setEmail(e.target.value)} className="input-sharp mt-2" data-testid="cw-email" placeholder="jane@example.com" />
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer pt-7" data-testid="cw-consent-label">
                      <input
                        type="checkbox"
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                        className="mt-1 h-5 w-5 accent-[#00E5FF] cursor-pointer flex-shrink-0"
                        data-testid="cw-consent"
                      />
                      <span className="text-sm text-slate-700">
                        <span className="font-bold text-[#0B132B]">Consent to contact</span>
                        <span className="block text-xs text-slate-500 mt-0.5">
                          Customer agrees to be contacted about this quote and related offers.
                        </span>
                      </span>
                    </label>
                  </div>
                </StepWrap>
              )}
            </AnimatePresence>

            <div className="flex items-center justify-between p-6 border-t border-slate-200 bg-slate-50">
              <button onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1} className="btn-outline-navy disabled:opacity-30" data-testid="cw-back">
                <ChevronLeft className="h-4 w-4" /> Back
              </button>
              {step < stepDefs.length ? (
                <button onClick={() => setStep((s) => s + 1)} disabled={!canNext()} className="btn-navy disabled:opacity-30" data-testid="cw-next">
                  Next <ChevronRight className="h-4 w-4" />
                </button>
              ) : (
                <button onClick={save} disabled={saving || billRows.length === 0} className="btn-brand disabled:opacity-50" data-testid="cw-save">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save bundle
                </button>
              )}
            </div>
          </div>

          {/* Live summary */}
          <aside className="lg:col-span-5">
            <div className="sticky top-24 bg-[#0B132B] text-white p-8 border-t-4 border-[#00E5FF] shadow-xl" data-testid="cw-summary">
              <div className="tw-overline !text-[#00E5FF]">Bundle summary</div>
              <div className="mt-2 text-sm text-slate-300">
                {totalLines === 0 ? "Pick lines per device type…" : `${totalLines} line${totalLines > 1 ? "s" : ""} across ${categoryGroups.length} ${categoryGroups.length === 1 ? "category" : "categories"}`}
              </div>

              {hasPhones && (
                <div
                  className={`mt-3 inline-flex items-center gap-2 px-2.5 py-1 mono text-[10px] uppercase tracking-widest border ${
                    idCapStats.atCap
                      ? "border-amber-400 text-amber-300 bg-amber-400/10"
                      : "border-slate-700 text-slate-300"
                  }`}
                  data-testid="cw-summary-idcap"
                  title="Finance, BYO, and Existing phone lines do not consume an ID-verify slot."
                >
                  <IdCard className="h-3 w-3" />
                  ID-verify · {idCapStats.used} / {idCapStats.cap}
                  {idCapStats.atCap && <span className="ml-1 text-amber-300">cap reached</span>}
                </div>
              )}

              <div className="hairline-divider !border-slate-700 my-6" />

              <div className="space-y-4 text-sm">
                {categoryGroups.length === 0 ? (
                  <p className="text-slate-400 italic">No lines selected yet.</p>
                ) : (
                  categoryGroups.map((g) => {
                    const groupRows = billRows.filter((r) => r.category_id === g.cat.id);
                    const groupLineEvals = allLineEvals.filter((L) => L.cat.id === g.cat.id);
                    const groupTotal = groupRows.reduce((s, r) => s + r.line_price, 0);
                    return (
                      <div key={g.cat.id} data-testid={`cw-sidebar-group-${g.cat.id}`}>
                        <div className="flex items-baseline justify-between gap-2 mb-1">
                          <div className="tw-overline !text-slate-400">{g.cat.name}</div>
                          <div className="mono text-xs text-slate-400">{g.lines} line{g.lines > 1 ? "s" : ""}</div>
                        </div>
                        {groupRows.length === 0 ? (
                          <div className="space-y-1">
                            {groupLineEvals.map((L, i) => (
                              <div key={i} className="text-xs text-slate-400">
                                Line {L.idx + 1} — {L.eval.device ? `${L.eval.device.brand} ${L.eval.device.model}` : "Pick device…"}
                              </div>
                            ))}
                            <p className="text-xs text-slate-500 italic mt-1">Pick plan…</p>
                          </div>
                        ) : (
                          <>
                            {groupRows.map((r, i) => (
                              <div key={i}>
                                <Row
                                  label={`Line ${r.line_number} — ${r.plan_name}`}
                                  value={`$${r.line_price.toFixed(2)}`}
                                  muted={i > 0}
                                />
                                {r.device_name && (
                                  <div className="text-[10px] text-slate-400 mono uppercase tracking-widest -mt-0.5 ml-1">
                                    {r.device_name}{r.device_scenario_label ? ` · ${r.device_scenario_label}` : ""}
                                  </div>
                                )}
                                {r.discount_label && (
                                  <div className="text-[10px] text-[#00E5FF] mono uppercase tracking-widest -mt-0.5 ml-1">
                                    {r.original_price != null
                                      ? <>was ${r.original_price.toFixed(2)} · {r.discount_label}</>
                                      : r.discount_label}
                                  </div>
                                )}
                              </div>
                            ))}
                            <div className="flex items-baseline justify-between pt-1">
                              <span className="text-slate-300 text-xs mono uppercase tracking-widest">{g.cat.name} subtotal</span>
                              <span className="mono ticker-num font-bold text-white">${groupTotal.toFixed(2)}</span>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              <div className="hairline-divider !border-slate-700 my-6" />

              {/* TWO-COLUMN TOTALS */}
              <div className="grid grid-cols-2 gap-4" data-testid="cw-summary-split">
                <div className="bg-white/[0.04] border border-slate-700 p-4" data-testid="cw-summary-today">
                  <div className="tw-overline !text-[#00E5FF]">Today's price</div>
                  <div className="mt-3 space-y-1.5 text-xs">
                    {deviceTotals.upfront > 0 && <Row label="Devices" value={`$${deviceTotals.upfront.toFixed(2)}`} muted />}
                    {planToday > 0 && <Row label="Plan today" value={`$${planToday.toFixed(2)}`} muted />}
                    {addOnLines.filter((l) => l.charge_first_month_today && (l.price || 0) > 0).map((l, i) => (
                      <Row key={`tod-${i}`} label={l.name} value={`$${l.price.toFixed(2)}`} muted />
                    ))}
                    {todayOnlyAddOnRows.map((l, i) => (
                      <Row key={`otp-${i}`} label={l.name} value={`$${l.one_time_price.toFixed(2)}`} muted />
                    ))}
                    {deviceTotals.tax > 0 && <Row label="Sales tax" value={`$${deviceTotals.tax.toFixed(2)}`} muted />}
                    {deviceTotals.accessCharge > 0 && (
                      <Row
                        label="Access charge"
                        value={`$${deviceTotals.accessCharge.toFixed(2)}`}
                        muted
                      />
                    )}
                    {(deviceTotals.total + planToday + addOnsToday) === 0 && (
                      <p className="text-slate-500 italic text-xs">No upfront charges yet.</p>
                    )}
                  </div>
                  <div className="mt-4 pt-3 border-t border-slate-700">
                    <div className="tw-overline !text-slate-400 mb-1">Due today</div>
                    <div className="mono text-2xl sm:text-3xl font-bold text-white ticker-num leading-none" data-testid="cw-total-onetime">
                      ${(deviceTotals.total + planToday + addOnsToday).toFixed(2)}
                    </div>
                  </div>
                </div>

                <div className="bg-white/[0.04] border border-slate-700 p-4" data-testid="cw-summary-monthly">
                  <div className="tw-overline !text-[#00E5FF]">Monthly price</div>
                  <div className="mt-3 space-y-1.5 text-xs">
                    <Row label="Plan monthly" value={`$${planMonthly.toFixed(2)}`} muted />
                    {deviceTotals.financeMonthly > 0 && (
                      <Row label="Device finance" value={`$${deviceTotals.financeMonthly.toFixed(2)}`} muted />
                    )}
                    {addOnLines.filter((l) => (l.price || 0) !== 0).map((l, i) => (
                      <Row
                        key={i}
                        label={l.name}
                        value={`${l.price < 0 ? "−" : ""}$${Math.abs(l.price).toFixed(2)}`}
                        muted
                      />
                    ))}
                  </div>
                  <div className="mt-4 pt-3 border-t border-slate-700">
                    <div className="tw-overline !text-slate-400 mb-1">Total / month</div>
                    <div className="mono text-2xl sm:text-3xl font-bold text-[#00E5FF] ticker-num leading-none" data-testid="cw-total-monthly">
                      ${(planMonthly + deviceTotals.financeMonthly + addOnsTotal).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>

              <p className="mt-6 text-xs text-slate-400">Plan total is tax-inclusive. Device sales tax & phone access charge come from admin settings.</p>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// LineCard — one card per line, swaps shape based on category kind
// ────────────────────────────────────────────────────────────────────────────
function LineCard({ L, carriers, carrierMap, usablePhones, usableTabletDevices, usableHIDevices, usableHotspotDevices, pricingForPhone, onUpdate }) {
  const Icon = iconForCategory(L.cat.name);
  const { kind, idx, state } = L;
  const labelMap = { phone: "Phone", tablet: "Tablet", hi: "Home Internet", hotspot: "Hotspot" };
  return (
    <div className="border-2 border-slate-200 p-5" data-testid={`line-card-${L.cat.id}-${idx + 1}`}>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 bg-[#0B132B] grid place-items-center"><Icon className="h-4 w-4 text-[#00E5FF]" /></div>
          <div>
            <div className="tw-overline">{labelMap[kind]}</div>
            <div className="font-bold text-[#0B132B]">Line {idx + 1}</div>
          </div>
        </div>
        <span className={`badge-pill ${L.eval.ready ? "badge-teal" : "badge-amber"}`} data-testid={`line-status-${L.cat.id}-${idx + 1}`}>
          {L.eval.ready ? "Ready" : "Incomplete"}
        </span>
      </div>

      {kind === "phone" && (
        <PhoneLineBody
          state={state} usablePhones={usablePhones} pricingForPhone={pricingForPhone}
          carriers={carriers} carrierMap={carrierMap}
          ev={L.eval}
          dataTestPrefix={`pl-${L.cat.id}-${idx + 1}`}
          onUpdate={onUpdate}
        />
      )}
      {kind === "tablet" && (
        <SimpleDeviceLineBody
          kind="tablet" state={state} devices={usableTabletDevices} ev={L.eval}
          dataTestPrefix={`tl-${L.cat.id}-${idx + 1}`}
          onUpdate={onUpdate}
        />
      )}
      {kind === "hi" && (
        <SimpleDeviceLineBody
          kind="hi" state={state} devices={usableHIDevices} ev={L.eval}
          dataTestPrefix={`hl-${L.cat.id}-${idx + 1}`}
          onUpdate={onUpdate}
        />
      )}
      {kind === "hotspot" && (
        <HotspotLineBody
          state={state} devices={usableHotspotDevices}
          ev={L.eval}
          dataTestPrefix={`htl-${L.cat.id}-${idx + 1}`}
          onUpdate={onUpdate}
        />
      )}
    </div>
  );
}

function PhoneLineBody({ state, usablePhones, pricingForPhone, carriers, ev, dataTestPrefix, onUpdate }) {
  const { device, options, financeAvailable, recommended, activeTier, qualifications, beyondIdCap, financeCapReached, isByo } = ev;
  return (
    <div className="space-y-5">
      {/* Device picker */}
      <div>
        <div className="tw-overline mb-2">Device</div>
        {beyondIdCap && (
          <div className="mb-2 p-3 bg-amber-50 border-l-4 border-amber-400 text-xs text-amber-900" data-testid={`${dataTestPrefix}-id-cap-note`}>
            <strong className="mono uppercase tracking-widest text-[10px] text-amber-700">Beyond ID-verify cap</strong>
            <div className="mt-1">This line has passed the promo-phone ID-verification limit. Only <strong>No Hassle</strong> or <strong>Full Retail</strong> can apply.</div>
          </div>
        )}
        {financeCapReached && !beyondIdCap && (
          <div className="mb-2 p-3 bg-amber-50 border-l-4 border-amber-400 text-xs text-amber-900" data-testid={`${dataTestPrefix}-finance-cap-note`}>
            <strong className="mono uppercase tracking-widest text-[10px] text-amber-700">Finance cap reached</strong>
            <div className="mt-1">This account has already hit the finance-device limit. This line can only pick a promo / full-retail path.</div>
          </div>
        )}
        {usablePhones.length === 0 ? (
          <EmptyNotice title="No phones configured" body="Admin needs to add a Promo with phone-pricing scenarios." />
        ) : (
          <DeviceSelect
            devices={usablePhones}
            value={state?.deviceId || null}
            onChange={(id) => onUpdate({ deviceId: id })}
            placeholder="Search or select a phone…"
            meta={(p) => `from $${minOptionPrice(pricingForPhone(p)).toFixed(2)}`}
            testid={`${dataTestPrefix}-device`}
            Icon={Smartphone}
          />
        )}
      </div>

      {/* Deal questions only after a non-BYO device is picked */}
      {device && isByo && (
        <div className="bg-[#0B132B] text-white p-4 border-l-4 border-[#00E5FF]" data-testid={`${dataTestPrefix}-byo-banner`}>
          <div className="tw-overline !text-[#00E5FF]">{device.brand} {device.model}</div>
          <p className="mt-1 text-xs text-slate-300">Customer brings/keeps this device — no deal flow required. Line counts toward the plan only.</p>
        </div>
      )}
      {device && !isByo && (
        <>
          {financeAvailable && (
            <div>
              <div className="tw-overline mb-2 flex items-center gap-2"><CreditCard className="h-3 w-3" /> Qualify for financing?</div>
              <div className="flex gap-2 flex-wrap">
                <Chip selected={state?.wantsFinance === true} onClick={() => onUpdate({ wantsFinance: true })} testid={`${dataTestPrefix}-fin-yes`}>Yes</Chip>
                <Chip selected={state?.wantsFinance === false} onClick={() => onUpdate({ wantsFinance: false })} testid={`${dataTestPrefix}-fin-no`}>No</Chip>
              </div>
            </div>
          )}

          {/* ID + Port-in only appear when finance isn't on the table OR the user explicitly declined it. */}
          {(!financeAvailable || state?.wantsFinance === false) && (
            <>
              <div>
                <div className="tw-overline mb-2 flex items-center gap-2"><IdCard className="h-3 w-3" /> Valid government ID?</div>
                <div className="flex gap-2 flex-wrap">
                  <Chip selected={state?.hasId === true} onClick={() => onUpdate({ hasId: true })} testid={`${dataTestPrefix}-id-yes`}>Yes</Chip>
                  <Chip selected={state?.hasId === false} onClick={() => onUpdate({ hasId: false })} testid={`${dataTestPrefix}-id-no`}>No</Chip>
                </div>
              </div>

              <div>
                <div className="tw-overline mb-2 flex items-center gap-2"><Repeat className="h-3 w-3" /> Bringing your number?</div>
                <div className="flex gap-2 flex-wrap">
                  <Chip selected={state?.porting === true} onClick={() => onUpdate({ porting: true })} testid={`${dataTestPrefix}-port-yes`}>Yes, port-in</Chip>
                  <Chip selected={state?.porting === false} onClick={() => onUpdate({ porting: false, fromCarrierId: "" })} testid={`${dataTestPrefix}-port-no`}>No, new number</Chip>
                </div>
                {state?.porting === true && (
                  <div className="mt-3">
                    <label className="tw-overline">Current carrier</label>
                    <select className="input-sharp mt-1 max-w-xs" value={state?.fromCarrierId || ""} onChange={(e) => onUpdate({ fromCarrierId: e.target.value })} data-testid={`${dataTestPrefix}-carrier`}>
                      <option value="">— Select —</option>
                      {carriers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </>
          )}

          {qualifications && (
            <div className="bg-[#0B132B] text-white p-4 border-l-4 border-[#00E5FF]" data-testid={`${dataTestPrefix}-result`}>
              <div className="tw-overline !text-[#00E5FF]">Match</div>
              {recommended ? (
                <>
                  <div className="flex items-baseline justify-between mt-1.5 gap-3 flex-wrap">
                    <div>
                      <div className="text-xs text-slate-300">Best acquisition path</div>
                      <div className="text-xl font-extrabold mt-0.5">{recommended.label}</div>
                    </div>
                    <div className="mono text-3xl font-bold text-[#00E5FF] ticker-num">
                      ${(activeTier ? activeTier.price : recommended.priceForRecommend).toFixed(2)}{recommended.isFinance && <span className="text-sm font-normal text-slate-300">/mo</span>}
                    </div>
                  </div>
                  {recommended.isFinance && recommended.financeTiers?.length >= 1 && (
                    <>
                      <div className="mt-3 text-[10px] uppercase tracking-widest mono text-slate-400">Credit tier</div>
                      <div className="mt-1.5 grid grid-cols-3 gap-2" role="radiogroup">
                        {recommended.financeTiers.map((t) => {
                          const sel = (state?.financeTier || 1) === t.tier;
                          return (
                            <button
                              key={t.tier} type="button" role="radio" aria-checked={sel}
                              onClick={() => onUpdate({ financeTier: t.tier })}
                              className={`px-2 py-2 border-2 text-center transition ${sel ? "border-[#00E5FF] bg-[#00E5FF]/15" : "border-slate-700 hover:border-slate-500"}`}
                              data-testid={`${dataTestPrefix}-tier-${t.tier}`}
                            >
                              <div className={`mono text-[10px] uppercase tracking-widest ${sel ? "text-[#00E5FF]" : "text-slate-400"}`}>Tier {t.tier}</div>
                              <div className="mono text-sm font-bold text-white ticker-num">${t.price.toFixed(2)}/mo</div>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                  {recommended.group === "full_retail" && (
                    <div className="mt-3 p-2 border-l-4 border-amber-400 bg-amber-400/10 text-amber-100 text-xs">
                      <strong className="uppercase tracking-widest mono text-[10px] text-amber-300">Disclaimer</strong>
                      <div className="mt-1">Full Retail means no dealer compensation.</div>
                    </div>
                  )}
                </>
              ) : (
                <div className="mt-2 text-sm text-slate-300">No qualifying scenario for this combination. Try changing your answers.</div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SimpleDeviceLineBody({ kind, state, devices, ev, dataTestPrefix, onUpdate }) {
  // The variant is now auto-applied based on context (line index + phone presence).
  // Whenever the evaluated variant list resolves to exactly one option, sync state.variant.
  useEffect(() => {
    const onlyKey = ev.variants.length === 1 ? ev.variants[0].key : null;
    if (onlyKey && state?.variant !== onlyKey) {
      onUpdate({ variant: onlyKey });
    } else if (!onlyKey && state?.variant) {
      onUpdate({ variant: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ev.variants.map((v) => v.key).join("|")]);

  return (
    <div className="space-y-5">
      <div>
        <div className="tw-overline mb-2">Device</div>
        {devices.length === 0 ? (
          <EmptyNotice title="No devices configured" body={`Admin needs to add a Promo with ${kind === "tablet" ? "tablet" : "home internet"} pricing.`} />
        ) : (
          <DeviceSelect
            devices={devices}
            value={state?.deviceId || null}
            onChange={(id) => onUpdate({ deviceId: id, variant: null })}
            placeholder={`Search or select a ${kind === "tablet" ? "tablet" : "home internet device"}…`}
            testid={`${dataTestPrefix}-device`}
            Icon={Package}
          />
        )}
      </div>
      {ev.device && ev.isByo && (
        <div className="bg-[#0B132B] text-white p-4 border-l-4 border-[#00E5FF]" data-testid={`${dataTestPrefix}-byo-banner`}>
          <div className="tw-overline !text-[#00E5FF]">{ev.device.brand} {ev.device.model}</div>
          <p className="mt-1 text-xs text-slate-300">Customer brings/keeps this device — line counts toward the plan only.</p>
        </div>
      )}
      {ev.device && !ev.isByo && (
        <div>
          <div className="tw-overline mb-2">Deal</div>
          {ev.variants.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No deal variant configured for this device.</p>
          ) : (
            <div className="bg-[#0B132B] text-white p-4 border-l-4 border-[#00E5FF]" data-testid={`${dataTestPrefix}-variant-${ev.variants[0].key}`}>
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <div>
                  <div className="tw-overline !text-[#00E5FF]">{ev.variants[0].label}</div>
                  <div className="text-xs text-slate-300 mt-1">
                    {ev.variants[0].key === "bundle"
                      ? "Applied because this line qualifies for the phone bundle."
                      : "Standard price applies (no phone on the account, or beyond the bundle cap)."}
                  </div>
                </div>
                <div className="mono text-3xl font-bold text-[#00E5FF] ticker-num">
                  ${(ev.variants[0].price || 0).toFixed(2)}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HotspotLineBody({ state, devices, ev, dataTestPrefix, onUpdate }) {
  return (
    <div className="space-y-5">
      <div>
        <div className="tw-overline mb-2">Device</div>
        {devices.length === 0 ? (
          <EmptyNotice title="No hotspot devices" body="Admin needs to add hotspot devices." />
        ) : (
          <DeviceSelect
            devices={devices}
            value={state?.deviceId || null}
            onChange={(id) => onUpdate({ deviceId: id })}
            placeholder="Search or select a hotspot…"
            testid={`${dataTestPrefix}-device`}
            Icon={Router}
          />
        )}
      </div>
      {ev?.device && ev.promoPrice > 0 && (
        <div className="bg-[#0B132B] text-white p-4 border-l-4 border-[#00E5FF]" data-testid={`${dataTestPrefix}-promo`}>
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div>
              <div className="tw-overline !text-[#00E5FF]">Promo price</div>
              <div className="text-xs text-slate-300 mt-1">One-time hotspot device price.</div>
            </div>
            <div className="mono text-3xl font-bold text-[#00E5FF] ticker-num">${ev.promoPrice.toFixed(2)}</div>
          </div>
        </div>
      )}
      {ev?.device && !(ev.promoPrice > 0) && (
        <p className="text-sm text-slate-500 italic">No promo configured for this hotspot — line will count toward the plan only.</p>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
function CategorySection({ icon: Icon, title, subtitle, testKey, children }) {
  return (
    <div className="border-l-4 border-[#0B132B] pl-5" data-testid={testKey}>
      <div className="flex items-center gap-3 mb-1">
        <Icon className="h-5 w-5 text-[#0B132B]" />
        <h3 className="font-bold text-[#0B132B]">{title}</h3>
      </div>
      <p className="text-xs text-slate-500 mb-4">{subtitle}</p>
      {children}
    </div>
  );
}

function SharedPlanPicker({ plans, selectedId, onSelect, lines }) {
  return (
    <div className="space-y-3" data-testid="shared-plan-picker">
      {plans.map((p) => {
        const sel = p.id === selectedId;
        const subtotal = tieredPhonesSubtotal(p, lines);
        return (
          <button
            key={p.id}
            onClick={() => onSelect(p.id)}
            className={`w-full text-left p-4 border-2 transition-all ${sel ? "border-[#00E5FF] bg-[#00E5FF]/10" : "border-slate-200 hover:border-slate-400"}`}
            data-testid={`shared-plan-${p.id}`}
          >
            <div className="flex items-baseline justify-between gap-4">
              <div>
                <div className="font-bold text-[#0B132B]">{p.name}</div>
                <div className="text-xs mono text-slate-500 mt-1">
                  {p.unlimited ? "Unlimited" : `${p.data_gb} GB`} data
                  {p.hotspot_unlimited ? " · Unlimited hotspot" : (p.hotspot_gb > 0 ? ` · ${p.hotspot_gb} GB hotspot` : "")}
                </div>
              </div>
              <div className="text-right">
                <div className="mono text-2xl font-bold text-[#0B132B] ticker-num">${subtotal.toFixed(0)}<span className="text-xs text-slate-500 font-normal">/mo</span></div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">for {lines} line{lines > 1 ? "s" : ""}</div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function PerLinePlanPicker({ categoryId, categoryName, lines, selectedIds, onChange, bundleDiscount, eligibleFor }) {
  const setLine = (idx, planId) => {
    const next = [...selectedIds];
    while (next.length < lines) next.push(null);
    next[idx] = planId;
    onChange(next.slice(0, lines));
  };
  return (
    <div className="space-y-3" data-testid={`per-line-picker-${categoryId}`}>
      {bundleDiscount && (
        <div className="border-l-4 border-[#00E5FF] bg-[#00E5FF]/10 p-3 text-xs text-[#0B132B]">
          <strong className="mono uppercase tracking-widest">Phone-bundle saving:</strong> ${bundleDiscount.amount.toFixed(0)} off each {categoryName} line.
        </div>
      )}
      {Array.from({ length: lines }, (_, i) => {
        const plansForThisLine = eligibleFor(i) || [];
        return (
          <div key={i} className="border border-slate-200 p-3">
            <div className="tw-overline mb-2">Line {i + 1}</div>
            {plansForThisLine.length === 0 ? (
              <p className="text-xs text-slate-500 italic">No qualifying plans for the chosen device/variant.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {plansForThisLine.map((p) => {
                  const sel = selectedIds[i] === p.id;
                  const base = p.price_one_line || 0;
                  const discounted = bundleDiscount ? Math.max(0, base - bundleDiscount.amount) : base;
                  return (
                    <button
                      key={p.id} type="button"
                      onClick={() => setLine(i, p.id)}
                      className={`text-left px-3 py-2 border-2 transition ${sel ? "border-[#00E5FF] bg-[#00E5FF]/10" : "border-slate-200 hover:border-slate-400"}`}
                      data-testid={`per-line-plan-${categoryId}-${i + 1}-${p.id}`}
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-semibold text-[#0B132B] text-sm truncate">{p.name}</span>
                        <span className="text-right">
                          {bundleDiscount ? (
                            <>
                              <span className="mono text-[10px] line-through text-slate-400 mr-1">${base.toFixed(0)}</span>
                              <span className="mono text-sm font-bold text-[#0B132B] ticker-num">${discounted.toFixed(0)}</span>
                            </>
                          ) : (
                            <span className="mono text-sm font-bold text-[#0B132B] ticker-num">${base.toFixed(0)}</span>
                          )}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function StepWrap({ title, children }) {
  return (
    <motion.div initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }} transition={{ duration: 0.2 }} className="p-8">
      <div className="tw-overline">{title.split(" — ")[0]}</div>
      <h2 className="mt-2 text-2xl font-bold text-[#0B132B]">{title.split(" — ")[1]}</h2>
      <div className="mt-6">{children}</div>
    </motion.div>
  );
}

function Chip({ selected, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} className={`px-4 py-2 border-2 font-semibold text-sm transition ${selected ? "border-[#00E5FF] bg-[#00E5FF]/15 text-[#0B132B]" : "border-slate-300 hover:border-[#0B132B] text-slate-700"}`} data-testid={testid}>{children}</button>
  );
}

function Row({ label, value, muted }) {
  return (
    <div className="flex items-baseline justify-between text-sm">
      <span className={`${muted ? "text-slate-400" : "text-slate-200"}`}>{label}</span>
      <span className="mono ticker-num font-medium text-white">{value}</span>
    </div>
  );
}

function StepIndicator({ step, count }) {
  return (
    <div className="flex items-center gap-2" data-testid="cw-step-indicator">
      {Array.from({ length: count }, (_, i) => i + 1).map((n) => (
        <div key={n} className={`mono text-xs h-8 w-8 grid place-items-center border-2 ${n === step ? "border-[#00E5FF] bg-[#00E5FF] text-[#0B132B]" : n < step ? "border-[#0B132B] bg-[#0B132B] text-white" : "border-slate-300 text-slate-400"}`}>{n}</div>
      ))}
    </div>
  );
}

function EmptyNotice({ title, body }) {
  return (
    <div className="py-10 text-center border-2 border-dashed border-slate-300" data-testid="cw-empty-notice">
      <AlertTriangle className="h-7 w-7 mx-auto text-amber-500" />
      <p className="mt-3 text-slate-700 font-semibold">{title}</p>
      <p className="text-sm text-slate-500 mt-1">{body}</p>
    </div>
  );
}

function CenteredLoader() {
  return <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>;
}

function AddOnsStep({ addOns, categoryGroups, accountWideAddOns, accountAddOns, onToggleAccount, lineAddOns, onToggleLine, lineLevelAddOnsFor, taxRate, defaultTaxRate, onTaxRateChange, taxBasis, onTaxBasisChange }) {
  return (
    <div className="space-y-8" data-testid="addons-step">
      <div data-testid="sales-tax-section">
        <div className="tw-overline mb-3">Sales tax</div>
        <div className="border-2 border-slate-200 p-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="font-bold text-[#0B132B]">State sales tax rate</div>
            <p className="text-xs text-slate-500 mt-1 max-w-md">
              Set the customer's local rate. <strong>MSRP tax</strong> computes tax against each device's MSRP; <strong>Retail tax</strong> uses the actual promo/sale price.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <input
                type="number" step="0.01" min="0" max="50"
                value={taxRate ?? defaultTaxRate ?? 0}
                onChange={(e) => {
                  const v = e.target.value;
                  onTaxRateChange(v === "" ? null : parseFloat(v));
                }}
                className="input-sharp w-24 text-right"
                data-testid="sales-tax-input"
              />
              <span className="mono text-[#0B132B] font-bold">%</span>
            </div>
            <div className="inline-flex border-2 border-[#0B132B]" data-testid="tax-basis-toggle">
              <button
                type="button"
                onClick={() => onTaxBasisChange("msrp")}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-widest mono transition ${taxBasis === "msrp" ? "bg-[#0B132B] text-white" : "bg-white text-[#0B132B] hover:bg-slate-100"}`}
                data-testid="tax-basis-msrp"
              >
                MSRP tax
              </button>
              <button
                type="button"
                onClick={() => onTaxBasisChange("retail")}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-widest mono transition border-l-2 border-[#0B132B] ${taxBasis === "retail" ? "bg-[#0B132B] text-white" : "bg-white text-[#0B132B] hover:bg-slate-100"}`}
                data-testid="tax-basis-retail"
              >
                Retail tax
              </button>
            </div>
          </div>
        </div>
      </div>

      {accountWideAddOns.length > 0 && (
        <div data-testid="account-addons">
          <div className="tw-overline mb-3">Account-wide</div>
          <div className="space-y-2">
            {accountWideAddOns.map((a) => {
              const isDiscount = (a.monthly_price || 0) < 0;
              const sel = accountAddOns.has(a.id);
              const isTodayOnly = (a.monthly_price || 0) === 0 && (a.one_time_price || 0) > 0;
              return (
                <button
                  key={a.id} type="button"
                  onClick={() => onToggleAccount(a.id)}
                  className={`w-full text-left p-4 border-2 transition flex items-center justify-between gap-3 ${sel ? "border-[#00E5FF] bg-[#00E5FF]/10" : "border-slate-200 hover:border-slate-400"}`}
                  data-testid={`account-addon-${a.id}`}
                >
                  <span className="flex items-center gap-3">
                    <span className={`h-5 w-5 grid place-items-center border-2 ${sel ? "border-[#00E5FF] bg-[#00E5FF]" : "border-slate-300"}`}>
                      {sel && <span className="text-[10px] text-[#0B132B] font-bold">✓</span>}
                    </span>
                    <span className="font-semibold text-[#0B132B]">{a.name}</span>
                  </span>
                  <span className={`mono ticker-num font-bold ${isDiscount ? "text-[#008B99]" : "text-[#0B132B]"}`}>
                    {isTodayOnly
                      ? `$${(a.one_time_price || 0).toFixed(2)} today`
                      : `${isDiscount ? "−" : ""}$${Math.abs(a.monthly_price || 0).toFixed(2)}/mo${(a.one_time_price || 0) > 0 ? ` + $${a.one_time_price.toFixed(2)} today` : ""}`}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {categoryGroups.map((g) => {
        const available = lineLevelAddOnsFor(g.cat.id);
        if (available.length === 0) return null;
        return (
          <div key={g.cat.id} data-testid={`line-addons-${g.cat.id}`}>
            <div className="tw-overline mb-3">{g.cat.name} — per line</div>
            <div className="space-y-3">
              {Array.from({ length: g.lines }, (_, idx) => {
                const selSet = (lineAddOns[g.cat.id] || {})[idx];
                const sel = selSet && typeof selSet.has === "function" ? selSet : new Set();
                return (
                  <div key={idx} className="border border-slate-200 p-3" data-testid={`line-addons-row-${g.cat.id}-${idx + 1}`}>
                    <div className="tw-overline mb-2">Line {idx + 1}</div>
                    <div className="flex flex-wrap gap-2">
                      {available.map((a) => {
                        const isSel = sel.has(a.id);
                        const isDiscount = (a.monthly_price || 0) < 0;
                        return (
                          <button
                            key={a.id} type="button"
                            onClick={() => onToggleLine(g.cat.id, idx, a.id)}
                            className={`px-3 py-2 border-2 text-xs font-semibold transition ${isSel ? "border-[#00E5FF] bg-[#00E5FF]/15 text-[#0B132B]" : "border-slate-200 hover:border-slate-400 text-slate-700"}`}
                            data-testid={`line-addon-${g.cat.id}-${idx + 1}-${a.id}`}
                          >
                            {a.name}
                            <span className={`ml-1.5 mono ${isDiscount ? "text-[#008B99]" : "text-slate-500"}`}>
                              {isDiscount ? "−" : ""}${Math.abs(a.monthly_price || 0).toFixed(2)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      {addOns.length === 0 && (
        <p className="text-sm text-slate-500 italic">No add-ons configured. Admin can add them under Admin → Add-ons.</p>
      )}
    </div>
  );
}

function round2(n) { return Math.round(n * 100) / 100; }
